import { SyntheticEvent, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Colors,
  Spinner,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { useSubscribeEmail } from 'hooks/useSubscribeEmail';
import {
  ErrorMessage,
  SuccessMessage,
} from 'components/Subscribe/FlashMessages';
import { GeneralCTAInfo } from 'rest/contentful/types';
import { TrackedComponent, useAvroTracking } from 'tracking';
import { disableSubscribePopup } from 'data/User/UserSettingsContext';

const buttonProps = {
  text: 'Subscribe',
};

export const SubscribePopup = ({ ctaInfo }: { ctaInfo: GeneralCTAInfo }) => {
  const trackEvent = useTrackEvent();
  const { emitCTAClickEvent } = useAvroTracking();
  const { error, loading, success, submitEmail } = useSubscribeEmail();
  const [email, setEmail] = useState<string>('');

  const { name, rawText, location, goal, linkDestination } = ctaInfo;

  useEffect(() => {
    if (success || error) {
      emitCTAClickEvent({
        interaction_raw_text: buttonProps.text,
        destination_url_address: linkDestination,
        custom_tracking_fields: {
          component: TrackedComponent.SubscribePopup,
          success: success || !error,
        },
      });
    }
  }, [success, trackEvent, error, emitCTAClickEvent, linkDestination]);

  useEffect(() => {
    if (success) {
      disableSubscribePopup();
      trackEvent('call_to_action', {
        path: window.location.pathname,
        from_path: document.referrer,
        cta_name: name,
        cta_raw_text: rawText,
        cta_location: location,
        cta_goal: goal,
        link_destination: linkDestination,
        signup_type: '',
      });
    }
  }, [success, trackEvent, name, rawText, location, goal, linkDestination]);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorMessage>{ctaInfo.errorMessage}</ErrorMessage>;
  }

  if (success) {
    return <SuccessMessage>{ctaInfo.successMessage}</SuccessMessage>;
  }

  return (
    <Outer data-testid="subscribe-popup">
      <Heading>{ctaInfo.headline}</Heading>
      <div>
        <Details>{ctaInfo.detailsText}</Details>
        <form
          onSubmit={(e: SyntheticEvent) => {
            e.preventDefault();
            submitEmail(email);
          }}
        >
          <TextInputWrapper>
            <TextInput
              ariaLabel="Email address"
              isFullWidth={true}
              isRequired={true}
              onChange={(e: SyntheticEvent) => {
                const value = (e.target as HTMLInputElement).value;
                setEmail(value);
              }}
              onFocus={() =>
                trackEvent('call_to_action', {
                  path: window.location.pathname,
                  from_path: document.referrer,
                  cta_name: 'email_subscribe_intent',
                  cta_raw_text: 'never miss a post',
                  cta_location: ctaInfo.location,
                  cta_goal: ctaInfo.goal,
                  link_destination: '',
                  signup_type: '',
                })
              }
              placeholder="Email address"
              size="small"
              type="email"
            />
          </TextInputWrapper>
          <Button
            hasShadow={true}
            isFullWidth={true}
            size="small"
            type="submit"
          >
            {buttonProps.text}
          </Button>
        </form>
      </div>
    </Outer>
  );
};

const Outer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-left: 1px;

  p {
    margin-bottom: 10px;
  }

  ${mediaQueries.tablet} {
    align-items: center;
    flex-direction: row;
    p {
      margin-bottom: 0;
    }

    > p {
      margin-right: 7%;
    }
  }

  ${mediaQueries.desktop} {
    align-items: flex-start;
    flex-direction: column;

    p {
      margin-bottom: 10px;
    }
  }
`;

const Heading = styled.p`
  color: ${Colors.neutral800};
  ${Typography.paragraph1ShortBold}
`;

const Details = styled.p`
  color: #999;
  display: none;
  margin-bottom: 10px;
  ${Typography.smallPrint1}
  ${mediaQueries.desktop} {
    display: block;
  }
`;

const TextInputWrapper = styled.div`
  margin-bottom: 10px;
`;
